import { useEffect, useCallback, useRef, useMemo } from 'react'

import { useApp }     from '../../../app/AppContext.jsx'
import { useCatalog } from '../../catalog/context/CatalogContext.jsx'

import { useI18n }    from '../../../shared/i18n/useI18n.js'
import { useProductStrings } from '../../../shared/i18n/useProductStrings.js'

import { useSvgProductInteraction } from '../../../shared/hooks/useSvgProductInteraction.js'
import { 
  encodeColorModeLegendHoverValue, 
  resolveColorModeHoveredLegendKey }  from '../../catalog/utils/colorModeLegend.js' //move to shared/
import { getRotateToNorthAngleDeg } from '../../../shared/utils/geometryUtils.js'
import { getTierName }              from '../../../shared/utils/productFormatters.js'
import { isDisplayable }            from '../../../shared/utils/productUtils.js'
import { syncLegendHighlightDOM }   from '../../../shared/utils/colorModeLegend.js'
import { AVAILABILITY_STATUS }      from '../../../shared/constants/availabilityStatus.js'
import { COLOR_MODE, 
  COLOR_MODE_REGISTRY }             from '../../../shared/constants/colorMode.js'
import { FETCH_STATUS }             from '../../../shared/constants/fetchStatus.js'
import { SECTION_ID }               from '../../../shared/constants/sectionIds.js'

import { useTierSvgFiles }  from '../hooks/useTierSvgFiles.js'

import { AssetLoadError } from '../../../shared/ui/AssetLoadError/index.js'
import { Button, ButtonGroup } from '../../../shared/ui/Button/index.js'
import { ChevronUp }      from '../../../shared/ui/icons/ChevronUp.jsx'
import { ChevronDown }    from '../../../shared/ui/icons/ChevronDown.jsx'
import { Close }          from '../../../shared/ui/icons/Close.jsx'
import { CompassNorth }   from '../../../shared/ui/icons/CompassNorth.jsx'
import { PreviewOverlay } from '../../hero/components/PreviewOverlay/PreviewOverlay.jsx' //move to shared/
import { Select }         from '../../../shared/ui/Select/index.js'
import { Skeleton }       from '../../../shared/ui/Skeleton/index.js'
import { ZoomableCanvas } from '../../../shared/ui/ZoomableCanvas/index.js'
import { ZoomIn }         from '../../../shared/ui/icons/ZoomIn.jsx'
import { ZoomOut }        from '../../../shared/ui/icons/ZoomOut.jsx'

import { TierSvgCanvas }    from './TierSvgCanvas.jsx'
import { TiersLegend }      from './TiersLegend/TiersLegend.jsx'

import styles from './TiersView.module.scss'

const MAX_BUILDINGS_BUTTONS = 9
const MAX_VISIBLE_TIERS     = 9
const TIER_BUTTON_HEIGHT    = 48  // px, must match CSS min-height

export function TiersView() {

  // ── i18n ──────────────────────────────────────────────────────────────────
  const { t } = useI18n()
  const getProductsCategoryString = useProductStrings();

  // ── Context reads ─────────────────────────────────────────────────────────
  const {
    appCapabilities,
    activeBuilding, setActiveBuilding,
    activeTier,     setActiveTier,
    activeProduct,  selectProductHandler,
    activateSectionHandler,
    isMobile,
    windowData,
    sectionRefs,
    logoSrc
  } = useApp()

  const {
    buildings, tiers, products, filteredProducts,
    activeFiltersConfig, defaultFiltersConfig, setActiveFiltersConfig,
    activeFiltersCount,
    colorMode, setColorMode, availableColorModes,
    colorModeDeciles,
    openFiltersUi,
    catalogCapabilities,
  } = useCatalog()

  // ── Local state ───────────────────────────────────────────────────────────
  const zoomRef = useRef(null)
  const tierLegendContainerRef = useRef(null) 
  const previewHandlersRef = useRef({ handlePreviewChange: () => {}, handleClosePreview: () => {} })
  const executeActionRef = useRef(() => {})

  const handlePreviewChange = useCallback((data) => {
    previewHandlersRef.current.handlePreviewChange(data)
  }, [])


  const handleRegisterPreviewHandler = useCallback((handlers) => {
    previewHandlersRef.current = handlers
    executeActionRef.current = handlers.handleExecuteAction 
  }, [])

  const handleOutlineAction = useCallback((mode, data) => {
    executeActionRef.current(mode, data)
  }, [])


  const handleProductHover = useCallback((product) => {
    if (!tierLegendContainerRef.current) return
    if (!product) {
      syncLegendHighlightDOM(tierLegendContainerRef.current, null)
      return
    }

    const modeConfig = COLOR_MODE_REGISTRY[colorMode]
    const key = resolveColorModeHoveredLegendKey({
      hoveredProduct: product,
      colorMode,
      colorModeDeciles,
      modeConfig,
    })
    const hoverValue = encodeColorModeLegendHoverValue(colorMode, key)
    
    syncLegendHighlightDOM(tierLegendContainerRef.current, hoverValue)
  }, [colorMode, colorModeDeciles])

  const handleLegendHoverChange = useCallback((value) => {
    if (!svgContainerRef.current) return
    if (value == null) {
      svgContainerRef.current.removeAttribute('data-legend-hover')
    } else {
      svgContainerRef.current.setAttribute('data-legend-hover', String(value))
    }
  }, [])


  // ── Derived state ─────────────────────────────────────────────────────────
  const { projectDataUrl } = windowData ?? {}

  const { 
    highlightSoldInRed,
    reservedUnitsAccessible,
    soldUnitsAccessible,
  } = appCapabilities;

  const { 
    areSoldOutTiersActive 
  } = catalogCapabilities;


  // ── Accessibility flags ─────────────────────────────────────────────────────
  const areSoldAccessible     = soldUnitsAccessible
  const areReservedAccessible = reservedUnitsAccessible

  const visibleSoldProducts = useMemo(
    () => (filteredProducts ?? []).filter(product => product.status === AVAILABILITY_STATUS.SOLD),
    [filteredProducts],
  )

  const showSoldHighlightNote = highlightSoldInRed
    && colorMode !== COLOR_MODE.STATUS
    && visibleSoldProducts.length > 0

  // ── Displayable products (for SVG interaction) ──────────────────────────────
  const displayableProducts = useMemo(
    () => products.filter(p => isDisplayable(p.visibility)),
    [products]
  )

  // ── Building's usable tiers ──────────────────────────────────────────────────
  const effectiveBuilding = activeBuilding ?? buildings?.[0]?.id_cad ?? null

  const buildingTiers = useMemo(
    () => tiers
      .filter(tier => tier.building_id === effectiveBuilding)
      .filter(tier => tier.isActive) //tiers disabled from viewing in CMS
      .filter(tier => !tier.isSoldOut || areSoldOutTiersActive || soldUnitsAccessible) //if sold-out tiers are disabled in CMS AND sold products are not accessible
      .sort((a, b) => a.ref_tier - b.ref_tier),
    [tiers, effectiveBuilding]
  )

  // ── Default to first building ───────────────────────────────────────────────
  useEffect(() => {
    if (!activeBuilding && buildings?.length) {
      setActiveBuilding(buildings[0].id_cad)
    }
  }, [activeBuilding, buildings, setActiveBuilding])

  // ── Tier SVG files ──────────────────────────────────────────────────────────
  const { tiersLayers, svgStatus } = useTierSvgFiles({
    projectDataUrl,
    buildings,
    tiers,
    activeBuilding: effectiveBuilding,
  })

  // ── Ensure activeTier is valid when building changes ────────────────────────
  useEffect(() => {
    if (!buildingTiers.length) return
    const refTiers = buildingTiers.map(tier => tier.ref_tier)
    if (activeTier == null || !refTiers.includes(activeTier)) {
      setActiveTier(refTiers[0] ?? null)
    }
  }, [buildingTiers, activeTier, setActiveTier])

  // ── Active SVG ──────────────────────────────────────────────────────────────
  const activeTierSvg = activeTier != null ? (tiersLayers[activeTier] ?? null) : null
  const svgError = svgStatus === FETCH_STATUS.ERROR ||
    (svgStatus === FETCH_STATUS.READY && activeTierSvg === null && activeTier != null)
  
  // ── Compass ─────────────────────────────────────────────────────────────────
  const activeBuildingObj = buildings.find(b => b.id_cad === effectiveBuilding)
  const compassAngle      = getRotateToNorthAngleDeg(activeBuildingObj?.azimuth ?? 90)


  // ── Outline mobile carousel (products mobile swipe) ────────────────────
  const getTierProductCandidates = useCallback(() => {
    // Kandydaci to po prostu wszystkie lokale z aktywnego piętra!
    return products.filter(p => p.building_id === activeBuilding && p.ref_tier === activeTier)
  }, [products, activeBuilding, activeTier])


  // ── SVG interaction hook ────────────────────────────────────────────────
  const { wireListeners } = useSvgProductInteraction({
    products:    products,
    filteredProducts,
    buildings,
    activeProduct,
    colorMode,
    colorModeDeciles,
    onProductClick:    handleOutlineAction,
    onPreviewChange:  handlePreviewChange,
    onProductHover:   handleProductHover,
    areSoldAccessible,
    areReservedAccessible,
    highlightSoldInRed,
    deviceLayout: { isMobile },
  })

  // ── Wire listeners after SVG mounts ─────────────────────────────────────────
  const svgContainerRef = useRef(null)

  useEffect(() => {
    if (!svgContainerRef.current) return
    return wireListeners(svgContainerRef.current)
  }, [activeTierSvg, activeProduct, filteredProducts, colorMode, wireListeners])

  // ── Close handler ───────────────────────────────────────────────────────────
  const handleClose = useCallback(() => {
    activateSectionHandler(SECTION_ID.HERO, { scrollTarget: SECTION_ID.HERO })
  }, [activateSectionHandler])

  // ── Tier scroll ─────────────────────────────────────────────────────────────
  const tiersScrollRef   = useRef(null)
  const scrollIntervalRef = useRef(null)
  const showTierChevrons  = buildingTiers.length > MAX_VISIBLE_TIERS

  // Reversed order: highest floor first (top)
  const reversedTiers = useMemo(
    () => [...buildingTiers].reverse(),
    [buildingTiers]
  )

  // Scroll active tier button into view (centered) when activeTier changes
  useEffect(() => {
    const container = tiersScrollRef.current
    if (!container || !showTierChevrons) return
    const revIdx = reversedTiers.findIndex(tier => tier.ref_tier === activeTier)
    if (revIdx < 0) return
    const scrollTarget = revIdx * TIER_BUTTON_HEIGHT - (container.clientHeight / 2) + (TIER_BUTTON_HEIGHT / 2)
    container.scrollTo({ top: Math.max(0, scrollTarget), behavior: 'smooth' })
  }, [activeTier, reversedTiers, showTierChevrons])

  const handleScrollTiers = useCallback((direction) => {
    const container = tiersScrollRef.current
    if (!container) return
    const delta = direction === 'up' ? -TIER_BUTTON_HEIGHT : TIER_BUTTON_HEIGHT
    container.scrollBy({ top: delta, behavior: 'smooth' })
  }, [])

  const startContinuousScroll = useCallback((direction) => {
    handleScrollTiers(direction)
    scrollIntervalRef.current = setInterval(() => handleScrollTiers(direction), 150)
  }, [handleScrollTiers])

  const stopContinuousScroll = useCallback(() => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current)
      scrollIntervalRef.current = null
    }
  }, [])


  // ── Renders ──────────────────────────────────────────────────────────────────
  // ── Building and tier switchers ───────────────────────────────────────────────────────
  
  const handleBuildingChange = useCallback((id_cad) => {
    setActiveBuilding(id_cad)
    // Clamp activeTier to the highest ref_tier available in the target building.
    // e.g. switching from building A (activeTier=7) to building B (max tier=4)
    // must not attempt to render B's tier 7 — it doesn't exist.
    const targetTiers = tiers.filter(tier => (tier.building_id === id_cad && tier.isActive))
    if (targetTiers.length > 0 && activeTier != null) {
      const maxTier = Math.max(...targetTiers.map(tier => tier.ref_tier))
      if (activeTier > maxTier) {
        setActiveTier(maxTier)
      }
    }
  }, [setActiveBuilding, setActiveTier, tiers, activeTier])

  const handleTierChange = useCallback((selectedTierValue) => {
    const nextTier = buildingTiers.find(
      tier => String(tier.ref_tier) === String(selectedTierValue)
    )?.ref_tier ?? null

    setActiveTier(nextTier)
  }, [buildingTiers, setActiveTier])

  
  function renderBuildingSelect() {
    if (!buildings.length) return null
    const activePhaseBuildings = buildings.filter(b => b.currently_on_sale) ?? []
    if (!activePhaseBuildings.length) return null    
    
    const sorted = [...activePhaseBuildings].sort((a, b) => a.id_cad.localeCompare(b.id_cad))
    return (
      <div className={styles.toolBox}>
        <span className={styles.toolLabel}>{t('common.building')}</span>
        <Select
          className={styles.switcherSelect}
          value={effectiveBuilding}
          onChange={handleBuildingChange}
          options={sorted.map(b => ({ value: b.id_cad, label: b.name_original }))}
        />
      </div>
    )
  }

  function renderBuildingSwitcher() {
    if (!buildings.length) return null
    const activePhaseBuildings = buildings.filter(b => b.currently_on_sale) ?? []
    if (!activePhaseBuildings.length) return null

    if (activePhaseBuildings.length > MAX_BUILDINGS_BUTTONS) {
      return renderBuildingSelect()
    }
    return (
      <div className={styles.buildingSwitcher}>
        <span className={styles.toolLabel}>{t('tiers.building_label')}</span>
        <ButtonGroup
          sliding
          size="large"
        >

          {activePhaseBuildings.map(b => (
            <Button
              key={b.id_cad}
              type="button"
              kind="icon"
              size="large"
              selected={b.id_cad === effectiveBuilding}
              onClick={() => handleBuildingChange(b.id_cad)}
              >
              {b.name_original}
            </Button>
          ))}
        </ButtonGroup>
      </div>
    )
  }

  function renderTierSelect() {
    if (!buildingTiers.length) return null
    const activeTierOptions = buildingTiers
      .map(t => ({
        value: t.ref_tier, 
        label: getTierName(t, true)
      }))

    return (
        <div className={styles.toolBox}>
        <span className={styles.toolLabel}>{t('common.floor')}</span>
        <Select
          className={styles.switcherSelect}
          value={activeTier}
          onChange={handleTierChange}
          options={activeTierOptions}
          placeholder={t('tiers.select_tier')}
        />
      </div>
    )
  }
  
  // ── Zoom ──────────────────────────────────────────────────────────────────

  const renderZoomControls = (size='large') => {
    return (
    <div className={styles.toolBox}>
      <span className={styles.toolLabel}>{t('common.zoom')}</span>
      <ButtonGroup size={size}>
        <Button
          type="button"
          kind="icon"
          size={size}
          variant="ghost"
          onClick={() => zoomRef.current?.zoomIn()}
          aria-label={t('tiers.zoom_in_aria')}
          >
          <ZoomIn />
        </Button>
        <Button
          type="button"
          kind="icon"
          size={size}
          variant="ghost"
          onClick={() => zoomRef.current?.zoomOut()}
          aria-label={t('tiers.zoom_out_aria')}
          >
          <ZoomOut />
        </Button>
      </ButtonGroup>
    </div>
  )};
  
  // ── Main render ──────────────────────────────────────────────────────────────────
  return (
    <section ref={sectionRefs.tiers} className={styles.tiersView}>

      {/* ── Top bar ── */}
      <div className={styles.topBar}>
        <span className={styles.heading}>{getProductsCategoryString('select', 'default')}</span>
        <div className={styles.toolsContainer}>
          { isMobile ? (
            <>
              <div className={styles.mobileSwitchers}>
                {renderBuildingSelect()}
                {renderTierSelect()}
              </div>
              {/* mobile Zoom +/- controls — rendered only when SVG is available */}
              {activeTierSvg && isMobile &&
                  renderZoomControls('medium')
              }
            </>
          ) : (
            <>
              {renderBuildingSwitcher()}
              <Button
                type="button"
                kind="icon"
                size="large"
                cta
                onClick={handleClose}
                aria-label={t('tiers.close_aria')}
              >
                <Close />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* ── Content ── */}
      <div className={styles.content}>

        {/* ── SVG area ── */}
        <div
          className={styles.svgArea}
          ref={svgContainerRef}
        >

          {/* Compass — SVG north indicator, rotated to true north */}
          <div className={styles.compass}>
            <CompassNorth
              width="100%" height="100%"
              style={{ transform: `rotate(${-compassAngle}deg)` }}
            />
          </div>

          {/* Loading skeleton */}
          {svgStatus === FETCH_STATUS.LOADING && !activeTierSvg && (
            <Skeleton className={styles.svgSkeleton} />
          )}

          {/* Error state */}
          {svgError && (
            <AssetLoadError logoSrc={logoSrc} />
          )}

          {/* SVG — key forces remount on tier/building change */}
          {activeTierSvg && (
            <ZoomableCanvas ref={zoomRef}>
              <TierSvgCanvas
                key={`${effectiveBuilding}-${activeTier}`}
                svgLayer={activeTierSvg}
              />
            </ZoomableCanvas>
          )}

          {/* ── Outline preview (building / tier / apartments hover) ───────────────────────── */}
          <PreviewOverlay
            activeTier={activeTier}
            currentIndexRef={null}
            getMobileProductCandidates={getTierProductCandidates}
            getMobileOutlineCandidates={null}
            goToBuildingModel={null}
            goToTierSection={null}
            svgContainerRef={svgContainerRef}
            onRegisterPreviewHandler={handleRegisterPreviewHandler}
          />
        </div>

        {/* ── Tier selector (right panel on desktop) — highest floor first ── */}
        { !isMobile && buildingTiers.length > 0 && (
          <div className={styles.tierSelector}>
            <span className={styles.tierLabel}>{t('tiers.tier_label')}</span>

            {showTierChevrons && (
              <Button
                type="button"
                kind="icon"
                size="large"
                variant="ghost"
                onMouseDown={() => startContinuousScroll('up')}
                onMouseUp={stopContinuousScroll}
                onMouseLeave={stopContinuousScroll}
                onTouchStart={() => startContinuousScroll('up')}
                onTouchEnd={stopContinuousScroll}
                aria-label={t('tiers.scroll_up_aria')}
              >
                <ChevronUp />
              </Button>
            )}

            <div
              className={styles.tierScrollContainer}
              ref={tiersScrollRef}
              style={showTierChevrons ? { maxHeight: `${MAX_VISIBLE_TIERS * TIER_BUTTON_HEIGHT}px` } : undefined}
            >
              {reversedTiers.map(tier => (
                <Button
                  key={tier.ref_tier}
                  type="button"
                  kind="icon"
                  size="large"
                  selected={tier.ref_tier === activeTier}
                  onClick={() => setActiveTier(tier.ref_tier)}
                >
                  {getTierName(tier, false)}
                </Button>
              ))}
            </div>

            {showTierChevrons && (
              <Button
                type="button"
                kind="icon"
                size="large"
                variant="ghost"
                onMouseDown={() => startContinuousScroll('down')}
                onMouseUp={stopContinuousScroll}
                onMouseLeave={stopContinuousScroll}
                onTouchStart={() => startContinuousScroll('down')}
                onTouchEnd={stopContinuousScroll}
                aria-label={t('tiers.scroll_down_aria')}
              >
                <ChevronDown />
              </Button>
            )}
          </div>
        )}
      </div>

      <div className={styles.bottomBar}>
        {/* Desktop Zoom +/- controls — rendered only when SVG is available */}
        {activeTierSvg && !isMobile &&
            renderZoomControls('large')
        }

        {/* ── Color legend (filter bar) ── */}
        <TiersLegend
          colorMode={colorMode}
          onColorModeChange={setColorMode}
          availableColorModes={availableColorModes}
          products={displayableProducts}
          activeProduct={activeProduct}
          activeFiltersConfig={activeFiltersConfig}
          defaultFiltersConfig={defaultFiltersConfig}
          setActiveFiltersConfig={setActiveFiltersConfig}
          onLegendHover={handleLegendHoverChange}
          tierLegendContainerRef={tierLegendContainerRef}
          colorModeDeciles={colorModeDeciles}
          activeFiltersCount={activeFiltersCount}
          showSoldHighlightNote={showSoldHighlightNote}
          onShowFilters={openFiltersUi}
        />
      </div>
    </section>
  )
}
